import type { MapFill, MapLayer } from "./mapDocument";
import { isJust, isNothing, just, nothing, type Maybe } from "./maybe";
import {
  nodeById,
  traceSmoothPath,
  type ScreenPoint,
  type WorldPoint,
} from "./mapGeometry";

export const MAP_FILL_ALPHA = 0.38;
export const MAP_FILL_HOVER_ALPHA = 0.18;

/** Canonical key for a closed node-id cycle (rotation + reflection invariant). */
export function cycleKey(ids: string[]): string {
  const n = ids.length;
  if (n === 0) return "";
  let best = "";
  for (const seq of [ids, [...ids].reverse()]) {
    for (let start = 0; start < n; start++) {
      const rot = [...seq.slice(start), ...seq.slice(0, start)].join("\0");
      if (best.length === 0 || rot < best) best = rot;
    }
  }
  return best;
}

export function fillPoints(
  layer: MapLayer,
  fill: Pick<MapFill, "nodeIds">,
): WorldPoint[] {
  const points: WorldPoint[] = [];
  for (const id of fill.nodeIds) {
    const node = nodeById(layer, id);
    if (isNothing(node)) return [];
    points.push({ x: node.value.x, y: node.value.y });
  }
  return points;
}

export function pointInPolygon(
  point: WorldPoint,
  polygon: WorldPoint[],
): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    const dy = b.y - a.y;
    const crosses =
      a.y > point.y !== b.y > point.y &&
      point.x <
        ((b.x - a.x) * (point.y - a.y)) / (dy !== 0 ? dy : Number.EPSILON) +
          a.x;
    if (crosses) inside = inside === false;
  }
  return inside;
}

export function signedPolygonArea(points: WorldPoint[]): number {
  let sum = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]!;
    const b = points[j]!;
    sum += (b.x + a.x) * (b.y - a.y);
  }
  return sum / 2;
}

/**
 * Smallest bounded face of the path graph that contains `point`.
 * Returns node ids in CCW order, or nothing if the click is not enclosed.
 */
export function findFillLoop(
  layer: MapLayer,
  point: WorldPoint,
): Maybe<string[]> {
  const adj = buildAdjacency(layer);
  const used = new Set<string>();
  const faces: { ids: string[]; area: number; points: WorldPoint[] }[] = [];

  for (const [from, nbrs] of adj) {
    for (const to of nbrs) {
      const startKey = directedKey(from, to);
      if (used.has(startKey)) continue;
      const ids = walkFace(layer, adj, from, to, used);
      if (isNothing(ids) || ids.value.length < 3) continue;
      const points = fillPoints(layer, { nodeIds: ids.value });
      if (points.length < 3) continue;
      const area = signedPolygonArea(points);
      if (area <= 1e-4) continue;
      faces.push({ ids: ids.value, area, points });
    }
  }

  let best: Maybe<{ ids: string[]; area: number }> = nothing;
  for (const face of faces) {
    if (pointInPolygon(point, face.points) === false) continue;
    if (isNothing(best) || face.area < best.value.area) best = just(face);
  }
  return isJust(best) ? just(best.value.ids) : nothing;
}

export function drawMapFill(
  ctx: CanvasRenderingContext2D,
  points: WorldPoint[],
  color: string,
  toScreen: (p: WorldPoint) => ScreenPoint,
  alpha = MAP_FILL_ALPHA,
): void {
  if (points.length < 3) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  traceSmoothPath(ctx, points, toScreen, true);
  ctx.fill();
  ctx.restore();
}

/** Back-to-front fill order (largest area first) so nested fills layer correctly. */
export function fillsBackToFront(layer: MapLayer): MapFill[] {
  return [...layer.fills].sort((a, b) => {
    const areaA = Math.abs(signedPolygonArea(fillPoints(layer, a)));
    const areaB = Math.abs(signedPolygonArea(fillPoints(layer, b)));
    return areaB - areaA;
  });
}

function directedKey(from: string, to: string): string {
  return `${from}>${to}`;
}

function buildAdjacency(layer: MapLayer): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  const link = (a: string, b: string): void => {
    if (a === b) return;
    if (adj.has(a) === false) adj.set(a, new Set());
    if (adj.has(b) === false) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  };
  for (const path of layer.paths) {
    for (let i = 0; i < path.nodeIds.length - 1; i++) {
      const a = path.nodeIds[i]!;
      const b = path.nodeIds[i + 1]!;
      if (isNothing(nodeById(layer, a)) || isNothing(nodeById(layer, b))) {
        continue;
      }
      link(a, b);
    }
  }
  return adj;
}

function neighborsCCW(
  layer: MapLayer,
  nodeId: string,
  neighborIds: Iterable<string>,
): string[] {
  const origin = nodeById(layer, nodeId);
  if (isNothing(origin)) return [];
  return [...neighborIds].sort((a, b) => {
    const pa = nodeById(layer, a);
    const pb = nodeById(layer, b);
    if (isNothing(pa) || isNothing(pb)) return 0;
    return (
      Math.atan2(pa.value.y - origin.value.y, pa.value.x - origin.value.x) -
      Math.atan2(pb.value.y - origin.value.y, pb.value.x - origin.value.x)
    );
  });
}

function walkFace(
  layer: MapLayer,
  adj: Map<string, Set<string>>,
  startFrom: string,
  startTo: string,
  used: Set<string>,
): Maybe<string[]> {
  const startKey = directedKey(startFrom, startTo);
  const loop: string[] = [startFrom];
  let from = startFrom;
  let to = startTo;
  for (let guard = 0; guard < 10_000; guard++) {
    const key = directedKey(from, to);
    if (used.has(key)) return nothing;
    used.add(key);
    loop.push(to);
    const nbrs = neighborsCCW(layer, to, adj.get(to) ?? []);
    const incoming = nbrs.indexOf(from);
    if (incoming < 0 || nbrs.length === 0) return nothing;
    const next = nbrs[(incoming + 1) % nbrs.length]!;
    from = to;
    to = next;
    if (directedKey(from, to) === startKey) break;
  }
  if (loop.length >= 2 && loop[0] === loop[loop.length - 1]) loop.pop();
  return loop.length >= 3 ? just(loop) : nothing;
}
